import React, { useState, useEffect } from "react";
import "../../assets/css/members.css";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import formatDate from "../../Components/DateFormat";
import VerticalSpace from "../../Components/VerticalSpace";
import TextFieldReport from "../../Components/TextFieldsReport/TextFieldReport";
import "../../stylesheets/indexpage.css";
import PaginationComponent from "../../Components/Common/PaginationComponent";
import { baseURL, postRequest } from "../../api/apiClient";
import Loader from "../../utils/Loading";
import Toast from "../../Components/Common/Toast";
import gsap from 'gsap';
import '../../stylesheets/common.css';

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 1100px;
`;

interface AnswerField {
  textfieldsChallengeParticlesId: number;
  userAnswer: string;
  keyword: string;
  status?: "approved" | "rejected" | null;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  active: boolean;
  fullName: string;
}

interface TextFieldsChallenge {
  id: number;
  question: string;
  points: number;
  active: boolean;
}

interface Theme {
  id: number;
  name: string;
  lessonId: number;
  order: number;
}

interface Lesson {
  id: number;
  level: string;
  title: string;
}

interface UserChallenge {
  id: number;
  status: string;
  attempts: number;
  completed: boolean;
}

interface TextFieldReportItem {
  id: number;
  textfieldsChallengeId: number;
  userId: number;
  themeId: number;
  userChallengeId: number;
  lessonId: number;
  data: AnswerField[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
  user: User;
  textfieldsChallenge: TextFieldsChallenge;
  theme: Theme;
  lesson: Lesson;
  userChallenge: UserChallenge;
}

const TextFieldsReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState<TextFieldReportItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0"));
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit") || "5"));
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [expandedReports, setExpandedReports] = useState<Set<number>>(new Set());

  // Sync state when browser Back/Forward changes the URL
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "0");
    const urlLimit = parseInt(searchParams.get("limit") || "5");
    setPage(urlPage);
    setLimit(urlLimit);
  }, [searchParams]);

  useEffect(() => {
    fetchTextFieldsReport(page + 1, limit);
  }, [page, limit]);

  // Fetch text fields report
  const fetchTextFieldsReport = async (page: number, perPage: number) => {
    setLoading(true);
    try {
      const url = `${baseURL}/textfield-reports/get-all-reports`;
      const body = {
        page,
        limit: perPage,
      };
      const response = await postRequest(url, body);
      setLoading(false);
      if (response?.data) {
        setReports(response.data.data.reports || []);
        setCount(response.data.data.totalReportsCount || 0);
        setPage(response.data.data.page - 1);
        setHasNextPage(response.data.data.hasNextPage || false);
        setHasPreviousPage(response.data.data.hasPreviousPage || false);
      }
    } catch (error) {
      console.error("Error fetching text fields report:", error);
      setLoading(false);
    }
  };

  const handleApprove = async (reportId: number, particleId: number) => {
    try {
      const response = await postRequest(
        `${baseURL}/textfield-reports/approve-reject`,
        { 
          reportId, 
          textfieldsChallengeParticlesId: particleId,
          action: 'approve'
        }
      );
      if (response?.data.success) {
        Toast("Answer approved successfully", "success");
        
        // Update the status of the specific field to "approved"
        setReports(prevReports => {
          return prevReports.map(report => {
            if (report.id === reportId) {
              const updatedData = report.data.map(field => {
                if (field.textfieldsChallengeParticlesId === particleId) {
                  return { ...field, status: "approved" as const };
                }
                return field;
              });
              return { ...report, data: updatedData };
            }
            return report;
          });
        });
      } else {
        Toast("Failed to approve answer", "error");
      }
    } catch (error) {
      console.error("Error approving answer:", error);
      Toast("Failed to approve answer", "error");
    }
  };

  const handleReject = async (reportId: number, particleId: number) => {
    try {
      const response = await postRequest(
        `${baseURL}/textfield-reports/approve-reject`,
        { 
          reportId, 
          textfieldsChallengeParticlesId: particleId,
          action: 'reject'
        }
      );
      if (response?.data.success) {
        Toast("Answer rejected successfully", "success");
        
        // Update the status of the specific field to "rejected"
        setReports(prevReports => {
          return prevReports.map(report => {
            if (report.id === reportId) {
              const updatedData = report.data.map(field => {
                if (field.textfieldsChallengeParticlesId === particleId) {
                  return { ...field, status: "rejected" as const };
                }
                return field;
              });
              return { ...report, data: updatedData };
            }
            return report;
          });
        });
      } else {
        Toast("Failed to reject answer", "error");
      }
    } catch (error) {
      console.error("Error rejecting answer:", error);
      Toast("Failed to reject answer", "error");
    }
  };

  const toggleExpanded = (reportId: number) => {
    setExpandedReports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    setSearchParams({ page: newPage.toString(), limit: limit.toString() });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    setSearchParams({ page: "0", limit: newLimit.toString() });
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2
    });
  }, [loading]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3">
              {/* Heading */}
              <div className="row mb-3 ">
                <div className="col">
                  <h2 style={{ fontWeight: "bold" }}>Text Field Report</h2>
                </div>
              </div>

              <ScrollableRowContainer>
                <InnerRowContainer>
                  <Container width="100%">
                    {/* Table Header */}
                    <RowContainer
                      alignItem="center"
                      padding="8px 12px 8px 12px"
                      gap="8px"
                      className="row-container"
                    >
                      <RowContainer flex="1" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                        </Label>
                      </RowContainer>
                      <RowContainer flex="11">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Question
                        </Label>
                      </RowContainer>
                      <RowContainer flex="8">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          User | Theme | Lesson | Level
                        </Label>
                      </RowContainer>
                      <RowContainer flex="4">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Created At
                        </Label>
                      </RowContainer>
                    </RowContainer>

                    {reports && reports.length > 0 ? (
                      <ColumnContainer>
                        {reports.map((report) => (
                          <TextFieldReport
                            key={report.id}
                            report={report}
                            onApprove={handleApprove}
                            onReject={handleReject}
                            isExpanded={expandedReports.has(report.id)}
                            onToggleExpanded={() => toggleExpanded(report.id)}
                          />
                        ))}
                      </ColumnContainer>
                    ) : (
                      <>
                        <VerticalSpace height={30} />
                        <Label
                          alignItems="center"
                          display="flex"
                          color="rgba(164, 163, 167, 1)"
                          justifyContent="center"
                        >
                          No reports found.
                        </Label>
                      </>
                    )}
                  </Container>
                </InnerRowContainer>
              </ScrollableRowContainer>
              <VerticalSpace height={10} />
              <RowContainer justifyContent="flex-end" className="user-data">
                <PaginationComponent
                  page={page}
                  rowsPerPage={limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={count}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              </RowContainer>
            </div>
            <VerticalSpace height={100} />
          </div>
        </>
      )}
    </>
  );
};

export default TextFieldsReport;
